import { Search } from 'lucide-react';
import React, { useCallback, memo } from 'react';

import {
  SIZE_SCALE,
  GAP_SCALE,
  PADDING_X,
  SHADOW_SCALE,
  COLOR_SCALE,
  INTERACTIVE_STATES,
} from '@/components/design-system/tokens';
import type { User } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';

import { DesktopNav } from './components/DesktopNav';
import { Logo } from './components/Logo';
import { MobileExpandableMenu } from './components/MobileExpandableMenu';
import { MobileMenuButton } from './components/MobileMenuButton';
import { PreferencesSelector } from './components/PreferencesSelector';
import { UserMenu } from './components/UserMenu';
import { useScrollBehavior } from './hooks/useScrollBehavior';

interface HeaderProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  setShowSearch: (show: boolean) => void;
  headerOnLight: boolean;
  currentPageName: string;
  providerBrandName?: string;
  providerBranding?: Record<string, unknown> | null; // Provider branding object
  showFindMyCareDropdown: boolean;
  setShowFindMyCareDropdown: (show: boolean) => void;
  user: User | null;
  newInquiryCount: number;
  pendingReportCount: number;
  userDropdownOpen: boolean;
  setUserDropdownOpen: (open: boolean) => void;
  userDropdownRef: React.RefObject<HTMLDivElement>;
  languageDropdownOpen: boolean;
  setLanguageDropdownOpen: (open: boolean) => void;
  languageDropdownRef: React.RefObject<HTMLDivElement>;
  handleLogout: () => void;
  handleSignup: () => void;
  handleLogin: () => void;
  currentLanguage: string;
  changeLanguage: (language: string) => void;
}

const Header = memo(
  ({
    mobileMenuOpen,
    setMobileMenuOpen,
    setShowSearch,
    headerOnLight,
    currentPageName,
    providerBrandName,
    providerBranding = null,
    showFindMyCareDropdown: _showFindMyCareDropdown,
    setShowFindMyCareDropdown: _setShowFindMyCareDropdown,
    user,
    newInquiryCount,
    pendingReportCount,
    userDropdownOpen,
    setUserDropdownOpen,
    userDropdownRef,
    languageDropdownOpen,
    setLanguageDropdownOpen,
    languageDropdownRef,
    handleLogout,
    handleSignup,
    handleLogin,
    currentLanguage,
    changeLanguage,
  }: HeaderProps) => {
    const { t } = useTranslation();
    const { scrollY, shouldHideHeader } = useScrollBehavior(currentPageName);

    // Memoize handlers
    const handleSignupClick = useCallback(() => handleSignup(), [handleSignup]);
    const handleLoginClick = useCallback(() => handleLogin(), [handleLogin]);
    const handleLogoutClick = useCallback(() => handleLogout(), [handleLogout]);

    // No shadow effect on scroll
    const shadowClass = SHADOW_SCALE.none;

    return (
      <header
        className={`fixed top-0 left-0 right-0 z-[50] ${INTERACTIVE_STATES.transitions.all} ${
          shouldHideHeader
            ? '-translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100 pointer-events-auto'
        } bg-white ${shadowClass}`}
        role="banner"
      >
        <div
          className={`flex justify-between items-center ${SIZE_SCALE['16']} ${PADDING_X['4']} sm:${PADDING_X['6']} lg:${PADDING_X['8']} relative z-[10000]`}
        >
          {/* Left Side: Logo */}
          <div className={`flex items-center ${GAP_SCALE['2']} lg:${GAP_SCALE['3']}`}>
            {/* Logo / Back Button */}
            <Logo
              currentPageName={currentPageName}
              headerOnLight={headerOnLight}
              providerBrandName={providerBrandName}
              providerBranding={providerBranding}
              scrollY={scrollY}
              t={t}
            />
          </div>

          {/* Center Navigation - Desktop Only */}
          <DesktopNav
            headerOnLight={headerOnLight}
            showFindMyCareDropdown={_showFindMyCareDropdown}
            setShowFindMyCareDropdown={_setShowFindMyCareDropdown}
            t={t}
          />

          {/* Right Side Controls */}
          <div className={`flex items-center ${GAP_SCALE['1']} sm:${GAP_SCALE['2']}`}>
            {/* Desktop User Menu */}
            <div className={`hidden lg:flex`}>
              <UserMenu
                user={user}
                userDropdownOpen={userDropdownOpen}
                setUserDropdownOpen={setUserDropdownOpen}
                userDropdownRef={userDropdownRef}
                handleLogout={handleLogoutClick}
                handleSignup={handleSignupClick}
                handleLogin={handleLoginClick}
                headerOnLight={headerOnLight}
                t={t}
              />
            </div>

            {/* Desktop Preferences Selector (Language & Currency) */}
            <div className={`hidden lg:flex`}>
              <PreferencesSelector
                languageDropdownOpen={languageDropdownOpen}
                setLanguageDropdownOpen={setLanguageDropdownOpen}
                languageDropdownRef={languageDropdownRef}
                currentLanguage={currentLanguage}
                changeLanguage={changeLanguage}
                headerOnLight={headerOnLight}
                t={t}
              />
            </div>

            {/* Search Button - Desktop only */}
            <button
              onClick={() => setShowSearch(true)}
              className={`
              hidden lg:flex items-center justify-center
              ${SIZE_SCALE.w9} ${SIZE_SCALE['9']} rounded-full
              ${INTERACTIVE_STATES.transitions.all}
              hover:scale-105 active:scale-95
              ${headerOnLight ? COLOR_SCALE['header-light'] : COLOR_SCALE['header-dark']}
            `}
              aria-label={t('aria_extended', 'search', 'Search')}
            >
              <Search className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`} />
            </button>

            {/* Mobile Expandable Menu - Consolidates User, Language, and Search */}
            <MobileExpandableMenu
              user={
                user
                  ? {
                      ...user,
                      partner_permissions: Boolean(user.partner_permissions),
                    }
                  : null
              }
              currentLanguage={currentLanguage}
              changeLanguage={changeLanguage}
              setShowSearch={setShowSearch}
              handleLogout={handleLogoutClick}
              handleSignup={handleSignupClick}
              handleLogin={handleLoginClick}
              headerOnLight={headerOnLight}
              pendingReportCount={pendingReportCount}
              t={t}
            />

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <MobileMenuButton
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
                headerOnLight={headerOnLight}
                user={user}
                newInquiryCount={newInquiryCount}
                t={t}
              />
            </div>
          </div>
        </div>
      </header>
    );
  }
);

Header.displayName = 'Header';

export default Header;
